/**
 * Ganzflaechige Einblendung fuer alle Verbindungszustaende ausser "verbunden".
 *
 * Grundsatz: Der Spieler soll immer in verstaendlicher Sprache erfahren,
 * was gerade passiert - niemals nur eine technische Fehlermeldung sehen.
 */

import { t } from '../i18n/index.js';
import type { MatchSnapshot } from '../net/MatchConnection.js';

interface Props {
  snapshot: MatchSnapshot;
  onRetry: () => void;
}

export function ConnectionOverlay({ snapshot, onRetry }: Props) {
  /*
   * Rueckkehr nach einem Abbruch (Abschnitt 30).
   *
   * Solange der Platz noch reserviert ist, wird nicht "getrennt" gemeldet,
   * sondern gezeigt, dass es weitergeht - mitsamt der Zeit, die noch bleibt.
   * Ein Knopf waere hier falsch: Es laeuft ja bereits von selbst.
   */
  if (snapshot.reconnecting) {
    return (
      <div className="dl-status">
        <h2 className="dl-status__headline">{t.connection.reconnecting}</h2>
        <p className="dl-status__detail">{t.connection.reconnectingHint}</p>
        <p className="dl-status__detail">
          {t.connection.reconnectSeconds(Math.ceil(snapshot.reconnectSecondsLeft))}
        </p>
      </div>
    );
  }

  if (snapshot.status === 'connected') return null;

  const headline =
    snapshot.status === 'connecting'
      ? t.connection.connecting
      : snapshot.status === 'error'
        ? t.connection.failed
        : t.connection.disconnected;

  const showRetry = snapshot.status === 'error' || snapshot.status === 'disconnected';

  return (
    <div className="dl-status">
      <h2 className="dl-status__headline">{headline}</h2>
      {snapshot.errorMessage && <p className="dl-status__detail">{snapshot.errorMessage}</p>}
      {showRetry && (
        <button type="button" className="dl-button" onClick={onRetry}>
          {t.connection.retry}
        </button>
      )}
    </div>
  );
}

/**
 * Hinweis waehrend einer Pause (Abschnitt 30).
 *
 * Anders als die Verbindungsanzeige liegt er nur oben im Bild: Das Match ist
 * ja nicht weg, es steht nur kurz still - und man soll weiter sehen, was auf
 * der Lane los ist.
 */
export function PauseNotice({ snapshot }: { snapshot: MatchSnapshot }) {
  if (!snapshot.pausedFor) return null;

  /*
   * Die Schicht liegt ueber allem und faengt jede Beruehrung ab.
   *
   * Waehrend der Pause steht das Spiel fuer beide still - dann darf auch
   * niemand nebenbei einkaufen oder eine Welle zusammenstellen
   * (Produktentscheidung vom 25.09.2026). Der Server weist solche Versuche
   * ohnehin ab; die Schicht sorgt dafuer, dass es gar nicht erst dazu kommt.
   */
  return (
    <div className="dl-pause-shield">
      <div className="dl-pause">
        <strong>
          {snapshot.pausedFor === 'self' ? t.connection.pausedSelf : t.connection.pausedOpponent}
        </strong>
        <span>{t.connection.pausedSeconds(Math.ceil(snapshot.pauseSecondsRemaining))}</span>
        <span className="dl-pause__hint">{t.connection.pausedFrozen}</span>
      </div>
    </div>
  );
}
